import { ChangeEvent, FC, Fragment, useCallback, useEffect, useState } from "react";
import { debounce } from "throttle-debounce";
import dayjs, { Dayjs } from "dayjs";
// i18next
import { useTranslation } from "react-i18next";
// Hooks
import useToggle from "app/hooks/useToggle";
import useAccountStatus from "app/hooks/useAccountStatus";
// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
// Types
import UserRoles from "app/types/UserRoles";
import CaseStatuses from "app/types/CaseStatuses";
// Async
import { getCalendarEvents } from "app/store/CalendarEvents/CalendarEventsAsync";
import { getInsuranceCasesList } from "app/store/Cases/Cases.async";
// Actions
import { CalendarEventsActions } from "app/store/CalendarEvents/CalendarEventsSlice";
import { CalendarEventTypesActions } from "app/store/CalendarEventTypes/CalendarEventTypesSlice";
import { CasesActions } from "app/store/Cases/Cases.slice";
// Selectors
import { selectCalendarEventsParams, selectCurrentDate, selectCurrentView } from "app/store/CalendarEvents/CalendarEventsSelectors";
import { selectMyUser } from "app/store/Users/Users.selectors";
import { selectCaseOptionsV2 } from "app/store/Cases/Cases.selectors";
import { selectCalendarEventTypes } from "app/store/CalendarEventTypes/CalendarEventTypesSelectors";
// MUI
import { Box } from "@mui/material";
import { CalendarPicker } from "@mui/x-date-pickers";
// Components
import { Button, Switch } from "app/components/Mui";
import { Autocomplete } from "app/components/Mui/Autocompletes";
import EventsFormDialog from "./EventsFormDialog";
import CalendarEventTypes from "./EventTypes";

type Props = {
  onDateChange: (newDate: Dayjs) => void;
};

const CalendarSidebar: FC<Props> = ({ onDateChange }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { isSuspended } = useAccountStatus();

  const myUser = useAppSelector(selectMyUser);
  const casesOptions = useAppSelector(selectCaseOptionsV2) || [];
  const params = useAppSelector(selectCalendarEventsParams);
  const currentDate = useAppSelector(selectCurrentDate);
  const currentView = useAppSelector(selectCurrentView);
  const eventTypes = useAppSelector(selectCalendarEventTypes);

  const [loaded, setLoaded] = useState<boolean>(false);
  const [stateParams, setStateParams] = useState(params);

  const handleChange = (field:any) => (fieldId:any) => {
    setStateParams((prevState: any) => ({
      ...prevState,
      [field]: fieldId,
    }));
  }

  const handleCheck = (e:ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setStateParams((prevState: any) => ({
      ...prevState,
      [name]: !checked,
    }));
  }

  const handleCheckType = (e:ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    const typeId = Number(name.split('-')[2]);
    setStateParams((prevState: any) => {
      const typeIds = checked
        ? [...prevState.typeIds, typeId]
        : prevState.typeIds.filter((id:number) => id !== typeId);
      return {
        ...prevState,
        typeIds,
      };
    });
  }

  // eslint-disable-next-line
  const debounceCases = useCallback(debounce(500, (search:string) => {
    dispatch(getInsuranceCasesList({ search, limit: 50, status: [CaseStatuses.Archived, CaseStatuses.Open, CaseStatuses.Pending, CaseStatuses.Waiting] }));
  }), []);

  const handleChangeCaseInput = (event:ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    debounceCases(value);
  }

  useEffect(() => {
    const { start, end } = getDateRangeByView({
      date: currentDate,
      view: currentView,
    });
    setStateParams((prevState: any) => ({
      ...prevState,
      start, end
    }));
  }, [currentView, currentDate]);

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      return;
    }
    dispatch(getCalendarEvents(stateParams));
    // eslint-disable-next-line
  }, [stateParams]);

  useEffect(() => {
    dispatch(getInsuranceCasesList({ limit: 50, status: [CaseStatuses.Archived, CaseStatuses.Open, CaseStatuses.Pending, CaseStatuses.Waiting] }));

    return () => {
      dispatch(CasesActions.resetCasesList());
      dispatch(CalendarEventsActions.setInitialField('events'));
      dispatch(CalendarEventsActions.setInitialField('params'));
      dispatch(CalendarEventTypesActions.setInitialField('eventTypes'));
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const typeIds = stateParams.typeIds || [];
    typeIds.forEach((typeId:number) => {
      if (!eventTypes?.some(eventType => eventType.id === typeId)) {
        setStateParams((prevState: any) => ({
          ...prevState,
          typeIds: prevState.typeIds.filter((id:number) => id !== typeId),
        }));
      }
    });
    // eslint-disable-next-line
  }, [eventTypes]);

  const { open, toggle } = useToggle();

  const isAdmin = myUser?.role === UserRoles.Admin;

  return (
    <Fragment>
      <Box sx={{ maxWidth: '350px', p: 2, mt: 2, height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <Button
          name="create-event"
          size="small"
          variant="contained"
          onClick={toggle}
          disabled={isSuspended}
        >{t('pages.companyCalendar.createButton')}</Button>
        <CalendarPicker
          date={currentDate}
          onChange={(newDate:Dayjs | null) => {
            if (newDate) {
              onDateChange(newDate);
            }
          }}
        />
        <CalendarEventTypes onChange={handleCheckType} />
        <Autocomplete
          size="small"
          value={stateParams.caseId}
          options={casesOptions}
          onChange={handleChange('caseId')}
          TextFieldProps={{
            label: t('labels.case'),
            name: 'caseId',
            onChange: handleChangeCaseInput
          }}
        />
        {isAdmin ? (
          <Switch
            name="includeAllEvents"
            value={!stateParams.includeAllEvents}
            onChange={handleCheck}
            label={t('pages.companyCalendar.adminOwnOnlyLabel')}
          />
        ) : null}
      </Box>

      {open ? (
        <EventsFormDialog open={open} onClose={toggle} />
      ) : null}
    </Fragment>
  );
};

export default CalendarSidebar;

export const getDateRangeByView = ({
  date,
  view,
}: { date: Dayjs | string, view: string } ): { start: string; end: string } => {
  let start: any;
  let end: any;

  const bufferDays = view === 'dayGridMonth' ? 7 : 0;

  switch (view) {
    case "dayGridMonth":
      start = dayjs(date).startOf("month").startOf("week");
      end = dayjs(date).endOf("month").endOf("week").add(1, "day");
      break;

    case "timeGridWeek":
      start = dayjs(date).startOf("week");
      end = dayjs(date).endOf("week").add(1, "day");
      break;

    case "timeGridDay":
      start = dayjs(date);
      end = dayjs(date).add(1, "day");
      break;

    case "multiMonthThree":
      start = dayjs(date).startOf("month");
      end = start.add(2, "month").endOf("month").add(1, "day");
      break;

    case "multiMonthYear":
      start = dayjs(date).startOf("month");
      end = start.add(11, "month").endOf("month");
      break;

    default:
      start = dayjs(date).startOf("month");
      end = dayjs(date).endOf("month").add(1, "day");
      break;
  }

  return {
    start: start.subtract(bufferDays, "day").format('YYYY-MM-DD'),
    end: end.add(bufferDays, "day").format('YYYY-MM-DD'),
  };
};
